import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../App';
import Layout from '../components/Layout';
import PullToRefresh from '../components/PullToRefresh';
import { authAxios } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export default function Leaderboard() {
  const { user, currentSeason, API } = useContext(AppContext);
  const [leaderboard, setLeaderboard] = useState([]);
  const [users, setUsers] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentSeason?.id) {
      loadData();
    }
  }, [currentSeason?.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const { data } = await authAxios.get(`${API}/seasons/${currentSeason.id}/leaderboard-bundle`);
      const leaderboardRes = { data: data.leaderboard };
      const episodesRes = { data: data.episodes };
      const usersRes = { data: data.users };
      const rostersRes = { data: data.rosters };
      
      // Get ALL unique user IDs from rosters (players with contestants assigned in this season)
      const rosterUserIds = [...new Set(rostersRes.data.map(r => r.user_id))];
      
      // Build users map, filtered to only users in this season
      const usersMap = {};
      usersRes.data.forEach(u => {
        // Include if user has this season in their season_ids OR has a roster entry
        const inSeason = u.season_ids?.includes(currentSeason.id) || rosterUserIds.includes(u.id);
        if (inSeason) {
          usersMap[u.id] = u;
        }
      });
      
      // Get all players who have rosters in this season (filtered)
      const allPlayersWithRosters = rosterUserIds.map(userId => usersMap[userId]).filter(u => u);
      
      // Merge leaderboard with all users who have rosters
      const leaderboardMap = new Map(leaderboardRes.data.map(entry => [entry.user_id, entry]));
      const fullLeaderboard = allPlayersWithRosters.map(u => {
        const existingEntry = leaderboardMap.get(u.id);
        return existingEntry || {
          user_id: u.id,
          user: u,
          total_points: 0,
          ep_points: {}
        };
      });
      
      // Sort by total points descending
      fullLeaderboard.sort((a, b) => (b.total_points || 0) - (a.total_points || 0));
      
      setLeaderboard(fullLeaderboard);
      setEpisodes(episodesRes.data);
      setUsers(allPlayersWithRosters);
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data
  const getChartData = () => {
    if (!episodes.length || !leaderboard.length) return [];
    
    return episodes.map(ep => {
      const epNum = ep.episode_number || ep.number || 0;
      const dataPoint = { episode: `Ep ${epNum}` };
      
      leaderboard.forEach(entry => {
        const userName = entry.user?.name || 'Unknown';
        // Calculate cumulative points up to this episode
        let cumulativePoints = 0;
        for (let i = 1; i <= epNum; i++) {
          cumulativePoints += (entry.ep_points?.[i.toString()] || 0);
        }
        dataPoint[userName] = cumulativePoints;
      });
      
      return dataPoint;
    });
  };

  const chartData = getChartData();
  
  // Generate colors for each player
  const colors = ['#f59e0b', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

  return (
    <Layout>
      <PullToRefresh onRefresh={loadData}>
        <div className="max-w-7xl mx-auto container-mobile" data-testid="leaderboard">
        <div className="spacing-section">
          <h1 className="heading-hero text-slate-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Leaderboard
          </h1>
          <p className="text-responsive text-slate-600">Season {currentSeason?.number} Standings</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-4 md:space-y-6">
            {/* Points Progression Chart */}
            {chartData.length > 0 && (
              <Card>
                <CardHeader className="padding-card pb-2 md:pb-4">
                  <CardTitle className="heading-card">Points Progression</CardTitle>
                </CardHeader>
                <CardContent className="padding-card pt-2">
                  <div className="chart-mobile-scroll">
                    <ResponsiveContainer width="100%" height={300} className="md:!h-[400px]">
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="episode" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        {leaderboard.map((entry, index) => (
                          <Line
                            key={entry.user_id}
                            type="monotone"
                            dataKey={entry.user?.name || 'Unknown'}
                            stroke={entry.user?.color || colors[index % colors.length]}
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            activeDot={{ r: 5 }}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Leaderboard Table */}
            <Card>
              <CardHeader className="padding-card pb-2 md:pb-4 block md:hidden">
                <CardTitle className="heading-card">Standings</CardTitle>
                <p className="text-xs text-slate-500 mt-1">Swipe left to see all episodes →</p>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th className="sticky left-0 bg-slate-50 z-20">Rank</th>
                        <th className="sticky left-0 bg-slate-50 z-20" style={{ left: '50px' }}>Player</th>
                        {episodes.map(ep => (
                          <th key={ep.id} className="text-center whitespace-nowrap">Ep {ep.episode_number || ep.number}</th>
                        ))}
                        <th className="text-center font-bold sticky right-0 bg-slate-50 z-20">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboard.length === 0 ? (
                        <tr>
                          <td colSpan={episodes.length + 3} className="text-center py-8 text-slate-600">
                            No players available
                          </td>
                        </tr>
                      ) : (
                        leaderboard.map((entry, index) => {
                          // Auto-calculate rank based on points
                          let rank = index + 1;
                          if (index > 0 && entry.total_points === leaderboard[index - 1].total_points) {
                            // Same points = same rank
                            rank = leaderboard.slice(0, index).findIndex(e => e.total_points === entry.total_points) + 1;
                          }
                          
                          return (
                            <tr
                              key={entry.user_id}
                              className={entry.user_id === user?.id ? 'bg-amber-50 font-semibold' : ''}
                            >
                              <td className="sticky left-0 bg-white z-10">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                                  rank === 1 ? 'bg-amber-400 text-white' :
                                  rank === 2 ? 'bg-slate-300 text-slate-700' :
                                  rank === 3 ? 'bg-orange-300 text-white' :
                                  'bg-slate-200 text-slate-600'
                                }`}>
                                  {rank}
                                </div>
                              </td>
                              <td className="sticky bg-white z-10" style={{ left: '50px' }}>
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-3 h-3 rounded-full flex-shrink-0" 
                                    style={{ backgroundColor: entry.user?.color || colors[index % colors.length] }}
                                  />
                                  <span>{entry.user?.name}</span>
                                  {entry.user_id === user?.id && <span className="text-amber-600 text-xs">(You)</span>}
                                </div>
                              </td>
                              {episodes.map(ep => (
                                <td key={ep.id} className="text-center">
                                  {entry.ep_points?.[(ep.episode_number || ep.number || 0).toString()] || '-'}
                                </td>
                              ))}
                              <td className="text-center font-bold text-lg text-amber-600 sticky right-0 bg-white z-10">
                                {entry.total_points || 0}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        </div>
      </PullToRefresh>
    </Layout>
  );
}
